import React, { useState, useRef, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { SxProps, Theme } from '@mui/system';
import ClearIcon from '@mui/icons-material/Clear';

import DatePickerRangeCustom from 'src/components/common/field/DatePickerRangeCustom';
import { useStore } from 'src/context/StoreContext';

type InputDatePickerRangeProps = {
  startDate: string | null;
  endDate: string | null;
  placeholder?: string;
  sx?: SxProps<Theme>;
  sxInput?: SxProps<Theme>;
  error?: boolean;
  onChange: (startDate: string | null, endDate: string | null) => void;
};

const InputDatePickerRange: React.FC<InputDatePickerRangeProps> = (props) => {
  const [open, setOpen] = useState(false);
  const storeContext = useStore();
  const store = storeContext.store;
  const ref = useRef<HTMLDivElement | null>(null);

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClose = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setOpen(false);
    }
  };

  const handleChange = (startDate: Date | null, endDate: Date | null) => {
    const formatDate = (date: Date | null) => {
      if (!date) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    };

    props.onChange(formatDate(startDate), formatDate(endDate));
  };

  const handleClear = () => {
    props.onChange(null, null);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClose);
    return () => {
      document.removeEventListener('mousedown', handleClose);
    };
  }, []);

  return (
    <Box position='relative' sx={{ ...props.sx }} ref={ref}>
      <OutlinedInput
        value={
          props.startDate && props.endDate
            ? `${props.startDate} ~ ${props.endDate}`
            : ''
        }
        fullWidth
        type={'text'}
        placeholder={props.placeholder}
        endAdornment={
          <InputAdornment position='end'>
            {props.startDate && props.endDate && (
              <IconButton
                aria-label='Select date range'
                edge='end'
                onClick={handleClear}
              >
                <ClearIcon
                  sx={{ color: '#A3AED0', width: '20px', height: '20px' }}
                />
              </IconButton>
            )}
            <IconButton aria-label='Select date range' edge='end'>
              <Box
                sx={{ width: '20px', height: '20px' }}
                component={'img'}
                src='/images/ic-date-picker.svg'
                alt='icon picker'
              />
            </IconButton>
          </InputAdornment>
        }
        sx={{
          width: '160px',
          height: '48px',
          '& .MuiOutlinedInput-notchedOutline': { 
            borderColor: props.error ? '#d32f2f !important' : ''
          },
          '& .MuiInputBase-input': {
            p: '0px 0px 0px 16px',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '14.4px',
          },
          '& input::placeholder': {
            color: '#AAB2BD',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '14.4px',
            opacity: 1,
          },
          backgroundColor: store.palette.input.bgColor,
          ...props.sxInput,
        }}
        inputProps={{
          sx: {
            color: store.palette.input.color,
            backgroundColor: store.palette.input.bgColor,
          },
        }}
        readOnly
        onClick={handleClick}
      />
      {open && (
        <Paper
          style={{
            position: 'absolute',
            zIndex: 10,
            width: '100%',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0)',
          }}
        >
          <DatePickerRangeCustom
            startDate={props.startDate ? new Date(props.startDate) : null}
            endDate={props.endDate ? new Date(props.endDate) : null}
            onChange={handleChange}
          />
        </Paper>
      )}
    </Box>
  );
};

export default InputDatePickerRange;
